import { useEffect } from "react";

const SITE = "https://villalobosprimeservicesllc.com";
const DEFAULT_IMAGE = `${SITE}/images/logo.png`;

interface SeoProps {
  title: string;
  description: string;
  path: string;
  type?: "website" | "article";
  image?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
}

/**
 * Imperative <head> manager.
 *
 * react-helmet-async does not update the document head in this build, so all
 * per-route title / description / canonical / Open Graph / JSON-LD tags are
 * managed directly here via the DOM. Existing tags (some are pre-rendered in
 * index.html) are updated in place to avoid duplicates.
 */
const upsertMeta = (selector: string, attr: string, key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const Seo = ({ title, description, path, type = "website", image, jsonLd, noindex = false }: SeoProps) => {
  const url = `${SITE}${path}`;
  const img = image || DEFAULT_IMAGE;

  useEffect(() => {
    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Canonical
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Open Graph
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta('meta[property="og:image"]', "property", "og:image", img);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "Villalobos Prime Services LLC");

    // Twitter
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", img);

    // JSON-LD structured data (replace any previously managed blocks)
    document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((n) => n.remove());
    const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    ldArray.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "jsonld");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((n) => n.remove());
    };
  }, [title, description, url, type, img, noindex, jsonLd]);

  return null;
};

export default Seo;
